import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QUIZ_QUESTIONS, QUIZ_RESULTS, WHATSAPP_LINK } from "../../lib/constants";
import { ChevronRight, Gift, Copy, Check, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";

const QuizSection = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [leadEmail, setLeadEmail] = useState("");
  const [leadWhatsApp, setLeadWhatsApp] = useState("");
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [couponCopied, setCouponCopied] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);

  const startQuiz = () => {
    setQuizStarted(true);
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
    setResult(null);
    setLeadSubmitted(false);
  };

  const handleAnswer = (option) => {
    const newAnswers = [...answers, option.value];
    setAnswers(newAnswers);

    if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      // Calculate result
      calculateResult(newAnswers);
    }
  };

  const calculateResult = (finalAnswers) => {
    // Simple scoring - count most frequent personality type
    const counts = {};
    finalAnswers.forEach((answer) => {
      // Map answers to result types
      let type;
      if (["classic", "morning", "intense"].includes(answer)) type = "classic";
      else if (["bold", "afternoon", "mix"].includes(answer)) type = "bold";
      else if (["sweet", "night", "caramel"].includes(answer)) type = "sweet";
      else type = "elegant";

      counts[type] = (counts[type] || 0) + 1;
    });

    const winningType = Object.keys(counts).reduce((a, b) =>
      counts[a] > counts[b] ? a : b
    );

    setResult(QUIZ_RESULTS[winningType]);
    setShowResult(true);

    // Celebration!
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ["#C88037", "#2D1810", "#E5984A"],
    });
  };

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!leadEmail && !leadWhatsApp) {
      toast.error("Ingresa tu email o WhatsApp para recibir tu cupón");
      return;
    }
    
    // Here you would send to backend
    setLeadSubmitted(true);
    toast.success("¡Listo! Tu cupón está desbloqueado");
    
    // More confetti!
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.7 },
      colors: ["#C88037", "#25D366"],
    });
  };

  const copyCoupon = () => {
    navigator.clipboard.writeText(result.coupon);
    setCouponCopied(true);
    toast.success("Cupón copiado al portapapeles");
    setTimeout(() => setCouponCopied(false), 2000);
  };

  const progress = ((currentQuestion + 1) / QUIZ_QUESTIONS.length) * 100;

  return (
    <section
      id="quiz"
      className="py-20 md:py-32 bg-[#F4EDE4]"
      data-testid="quiz-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatePresence mode="wait">
          {!quizStarted ? (
            // Quiz Intro
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              className="text-center"
            >
              <span className="text-[#C88037] font-medium text-sm uppercase tracking-wider">
                Quiz Interactivo
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2D1810] mt-2 mb-4">
                Descubre tu <span className="italic text-[#C88037]">Brownie Ideal</span>
              </h2>
              <p className="text-[#5E4B3C] max-w-xl mx-auto mb-8">
                Responde 3 preguntas rápidas y te revelaremos cuál es el brownie 
                que estaba destinado para ti. <strong>Además, recibirás un cupón de descuento.</strong>
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={startQuiz}
                className="bg-[#C88037] text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg shadow-[#C88037]/30 hover:bg-[#E5984A] transition-colors inline-flex items-center gap-2"
                data-testid="start-quiz-btn"
              >
                <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                Descubrir Mi Brownie
              </motion.button>
            </motion.div>
          ) : !showResult ? (
            // Quiz Questions
            <motion.div
              key={`question-${currentQuestion}`}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="bg-[#FDFBF7] rounded-3xl p-6 md:p-10 shadow-xl"
            >
              {/* Progress */}
              <div className="mb-8">
                <div className="flex justify-between text-sm text-[#5E4B3C] mb-2">
                  <span>Pregunta {currentQuestion + 1} de {QUIZ_QUESTIONS.length}</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="h-2 bg-[#E5DCD0] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    className="h-full bg-gradient-to-r from-[#C88037] to-[#E5984A] rounded-full"
                  />
                </div>
              </div>

              {/* Question */}
              <h3 className="font-serif text-2xl md:text-3xl text-[#2D1810] mb-8 text-center">
                {QUIZ_QUESTIONS[currentQuestion].question}
              </h3>

              {/* Options */}
              <div className="grid sm:grid-cols-2 gap-4">
                {QUIZ_QUESTIONS[currentQuestion].options.map((option) => (
                  <motion.button
                    key={option.id}
                    whileHover={{ scale: 1.02, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAnswer(option)}
                    className="quiz-option bg-[#F4EDE4] rounded-2xl overflow-hidden text-left group"
                    data-testid={`quiz-option-${option.id}`}
                  >
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={option.image}
                        alt={option.text}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4 flex items-center justify-between">
                      <span className="font-medium text-[#2D1810]">{option.text}</span>
                      <ChevronRight
                        className="w-5 h-5 text-[#C88037] group-hover:translate-x-1 transition-transform"
                        strokeWidth={1.5}
                      />
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          ) : (
            // Quiz Result
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#FDFBF7] rounded-3xl overflow-hidden shadow-xl"
            >
              <div className="grid md:grid-cols-2">
                {/* Image */}
                <div className="relative aspect-square md:aspect-auto">
                  <img
                    src={result.image}
                    alt={result.brownie}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2D1810]/80 to-transparent md:hidden" />
                </div>

                {/* Content */}
                <div className="p-6 md:p-10">
                  <div className="flex items-center gap-2 text-[#C88037] mb-4">
                    <Gift className="w-5 h-5" strokeWidth={1.5} />
                    <span className="text-sm font-medium uppercase tracking-wider">Tu Brownie Ideal</span>
                  </div>

                  <h3 className="font-serif text-3xl text-[#2D1810] mb-4">
                    {result.brownie}
                  </h3>

                  <p className="text-[#5E4B3C] mb-6">{result.description}</p>

                  {/* Coupon Section */}
                  {!leadSubmitted ? (
                    <form onSubmit={handleLeadSubmit} className="space-y-4">
                      <p className="text-sm text-[#2D1810] font-medium">
                        Deja tu contacto para desbloquear un <strong>{result.discount} de descuento</strong>:
                      </p>
                      <input
                        type="email"
                        value={leadEmail}
                        onChange={(e) => setLeadEmail(e.target.value)}
                        placeholder="Tu email"
                        className="w-full px-4 py-3 rounded-xl border border-[#E5DCD0] focus:border-[#C88037] focus:ring-1 focus:ring-[#C88037] outline-none"
                        data-testid="quiz-email-input"
                      />
                      <input
                        type="tel"
                        value={leadWhatsApp}
                        onChange={(e) => setLeadWhatsApp(e.target.value)}
                        placeholder="Tu WhatsApp (opcional)"
                        className="w-full px-4 py-3 rounded-xl border border-[#E5DCD0] focus:border-[#C88037] focus:ring-1 focus:ring-[#C88037] outline-none"
                        data-testid="quiz-whatsapp-input"
                      />
                      <button
                        type="submit"
                        className="w-full bg-[#C88037] text-white py-3 rounded-xl font-semibold hover:bg-[#E5984A] transition-colors"
                        data-testid="quiz-submit-lead"
                      >
                        Desbloquear Mi Cupón
                      </button>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-[#F4EDE4] rounded-xl p-4 flex items-center justify-between">
                        <div>
                          <span className="text-xs text-[#5E4B3C]">Tu cupón de {result.discount}:</span>
                          <div className="font-mono text-2xl font-bold text-[#C88037]">
                            {result.coupon}
                          </div>
                        </div>
                        <button
                          onClick={copyCoupon}
                          className="p-3 bg-white rounded-xl hover:bg-[#E5DCD0] transition-colors"
                          data-testid="copy-coupon-btn"
                        >
                          {couponCopied ? (
                            <Check className="w-5 h-5 text-green-500" strokeWidth={1.5} />
                          ) : (
                            <Copy className="w-5 h-5 text-[#2D1810]" strokeWidth={1.5} />
                          )}
                        </button>
                      </div>
                      <a
                        href={`${WHATSAPP_LINK}?text=${encodeURIComponent(`¡Hola! Hice el quiz y mi brownie ideal es ${result.brownie}. Quiero usar mi cupón ${result.coupon} 🍫`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-[#25D366] text-white py-3 rounded-xl font-semibold text-center hover:bg-[#20BD5A] transition-colors"
                        data-testid="quiz-order-whatsapp"
                      >
                        Pedir Mi Brownie Ideal
                      </a>
                    </div>
                  )}

                  {/* Restart */}
                  <button
                    onClick={startQuiz}
                    className="w-full mt-4 text-[#5E4B3C] text-sm hover:text-[#C88037] transition-colors"
                    data-testid="restart-quiz"
                  >
                    Volver a hacer el quiz
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default QuizSection;
